import React, { useEffect, useRef, useState } from "react";
import { BookMarked, Loader2, Sparkles, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { formatError } from "@/lib/auth";

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "");

function Proposal({ p, onDecide }) {
  const [text, setText] = useState(p.content);
  return (
    <div className="rounded-md border border-cyan-700/50 bg-cyan-950/20 p-3 space-y-2" data-testid={`brief-proposal-${p.id}`}>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <Badge variant="outline" className="text-cyan-300">Suggested update</Badge>
        <span className="text-muted-foreground">from {p.feedback_count} feedback items · {p.created_by?.name} · {when(p.created_at)}</span>
      </div>
      {p.changes.length > 0 && (
        <ul className="text-xs list-disc pl-5 space-y-0.5" data-testid={`brief-proposal-changes-${p.id}`}>
          {p.changes.map((c) => <li key={c}>{c}</li>)}
        </ul>
      )}
      <Textarea value={text} onChange={(e) => setText(e.target.value)} className="min-h-[260px] font-mono text-xs" data-testid={`brief-proposal-text-${p.id}`} />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onDecide(p.id, "approve", text)} data-testid={`brief-proposal-approve-${p.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
        <Button size="sm" variant="outline" onClick={() => onDecide(p.id, "reject")} data-testid={`brief-proposal-reject-${p.id}`}><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
      </div>
    </div>
  );
}

/** Admin: the house rules-writing brief every rule-writing prompt reads, plus AI-suggested updates to approve. */
export function BriefAdmin() {
  const [b, setB] = useState(null);
  const [draft, setDraft] = useState("");
  const poll = useRef(null);
  const apply = (r) => { setB(r); setDraft(r.content); };
  const load = () => api.getBrief().then(setB);
  useEffect(() => { api.getBrief().then(apply).catch((e) => toast.error(formatError(e))); return () => clearInterval(poll.current); }, []);
  useEffect(() => {
    clearInterval(poll.current);
    if (b?.proposing) poll.current = setInterval(() => load().catch(() => {}), 3000);
    if (b?.propose_error) toast.error(b.propose_error);
  }, [b?.proposing]);  // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, msg) => { try { apply(await fn()); if (msg) toast.success(msg); } catch (e) { toast.error(formatError(e)); } };
  if (!b) return <Loader2 className="w-4 h-4 animate-spin" />;
  return (
    <Card>
      <CardContent className="p-4 space-y-3" data-testid="brief-admin">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold flex items-center gap-2"><BookMarked className="w-4 h-4 text-cyan-400" /> Rules-writing brief</h3>
          <Badge variant="outline">v{b.version}</Badge>
          <span className="text-xs text-muted-foreground">updated {when(b.updated_at)} by {b.updated_by?.name}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Every Write-from-intent and revision prompt reads this brief, plus any feedback newer than it. Once {b.auto_after} new feedback items
          pile up, the AI drafts an update for you to approve (it uses the LLM key of whoever leaves the feedback).
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" data-testid="brief-new-feedback">{b.new_feedback} new feedback items</Badge>
          <Button size="sm" variant="outline" disabled={b.proposing || !b.new_feedback} onClick={() => act(api.proposeBrief)} data-testid="brief-propose-btn">
            {b.proposing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            {b.proposing ? "Writing an update…" : "Suggest an update from feedback"}
          </Button>
        </div>
        {b.proposals.map((p) => (
          <Proposal key={p.id} p={p} onDecide={(id, d, content) => act(() => api.decideBrief(id, d, content), d === "approve" ? "Brief updated" : "Suggestion rejected")} />
        ))}
        <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} className="min-h-[360px] font-mono text-xs" data-testid="brief-editor" />
        <Button size="sm" disabled={draft === b.content} onClick={() => act(() => api.saveBrief(draft), "Brief saved")} data-testid="brief-save-btn">Save brief</Button>
      </CardContent>
    </Card>
  );
}

/** Read-only view of the brief for everyone. */
export function BriefViewer() {
  const [b, setB] = useState(null);
  return (
    <Dialog onOpenChange={(o) => o && api.getBrief().then(setB).catch((e) => toast.error(formatError(e)))}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5" data-testid="brief-view-btn"><BookMarked className="w-3.5 h-3.5" /> Rules-writing brief</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Rules-writing brief {b && <span className="text-xs text-muted-foreground font-normal">v{b.version}</span>}</DialogTitle></DialogHeader>
        {!b ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <pre className="whitespace-pre-wrap text-xs font-mono text-foreground/90" data-testid="brief-view-content">{b.content}</pre>
        )}
      </DialogContent>
    </Dialog>
  );
}
